import {
  Add,
  AddCircleOutline,
  AutoAwesomeOutlined,
  CheckCircleOutlineOutlined,
  DashboardOutlined,
  SaveOutlined,
  Settings,
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { ExpandableSidebarLink } from '@omnea-app/fe-app-shell/sidebar/ExpandableSidebarLink'
import { SidebarContentLayout } from '@omnea-app/fe-app-shell/sidebar/SidebarContentLayout'
import { SidebarLink } from '@omnea-app/fe-app-shell/sidebar/SidebarLink'
import { SidebarBadge } from '@omnea-app/fe-app-shell/sidebar/StyledSidebarComponents'
import { hrefFor, type NavigableRouteKey } from '@omnea-app/fe-authz/routeHref'
import { useCanCreateRequest } from '@omnea-app/fe-authz/useCanCreateRequest'
import { useCanAccessRoute } from '@omnea-app/fe-authz/useRouteAccess'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { InternalLinkIconButton } from '@omnea-app/fe-routing/InternalLinkIconButton'
import { isRouteActive } from '@omnea-app/fe-routing/routes/isRouteActive'
import type { MenuItemType } from '@omnea-app/fe-app-shell/sidebar/SidebarUncontrolled'
import type { NewRequestInitiatedProperties } from '@omnea-app/fe-requests/analytics'

type AppSidebarContentProps = {
  isSidebarExpanded: boolean
  items: MenuItemType[]
  numberOfActiveTasks: number
}

const CREATE_REQUEST_HREF = hrefFor('startRequest')

export const AppSidebarContent = ({ isSidebarExpanded, items, numberOfActiveTasks }: AppSidebarContentProps) => {
  const { t } = useTranslation('common')
  const canAccessAi = useCanAccessRoute('ai')
  const canCreateRequest = useCanCreateRequest()

  const { asPath } = useRouter()
  const isSelected = (route: NavigableRouteKey) => isRouteActive(asPath, hrefFor(route))
  const trackCreateRequest = () => track('NewRequestInitiated', { source: 'RequestsCTA' } satisfies NewRequestInitiatedProperties)

  return (
    <SidebarContentLayout
      primaryItems={
        <>
          <SidebarLink
            href={hrefFor('dashboard')}
            title={t('sidebar.dashboard')}
            selected={isSelected('dashboard')}
            Icon={DashboardOutlined}
            isSidebarExpanded={isSidebarExpanded}
          />
          {canAccessAi ? (
            <SidebarLink
              href={hrefFor('ai')}
              title={t('sidebar.omnea-ai')}
              selected={isSelected('ai')}
              Icon={AutoAwesomeOutlined}
              isSidebarExpanded={isSidebarExpanded}
            />
          ) : null}
          <SidebarLink
            href={hrefFor('tasks')}
            title={t('sidebar.tasks')}
            selected={isSelected('tasks')}
            Icon={CheckCircleOutlineOutlined}
            isSidebarExpanded={isSidebarExpanded}
          >
            {numberOfActiveTasks > 0 ? (
              <Tooltip title={t('sidebar.task-no-tooltip', { count: numberOfActiveTasks })}>
                <SidebarBadge sx={!isSidebarExpanded ? { ml: -3.5, mt: -3.5 } : {}}>{numberOfActiveTasks}</SidebarBadge>
              </Tooltip>
            ) : null}
          </SidebarLink>
          {canCreateRequest ? (
            <SidebarLink
              href={hrefFor('startDrafts')}
              title={t('sidebar.drafts')}
              selected={isSelected('startDrafts')}
              Icon={SaveOutlined}
              isSidebarExpanded={isSidebarExpanded}
            />
          ) : null}
        </>
      }
      resourceItems={items.map((item) => {
        const { children, ...rest } = item
        if (children != null) {
          return <ExpandableSidebarLink {...rest} key={rest.href} items={children} isSidebarExpanded={isSidebarExpanded} />
        }
        return <SidebarLink {...rest} isSidebarExpanded={isSidebarExpanded} key={rest.href} />
      })}
      settingsItem={
        <SidebarLink
          href={`${hrefFor('settingsNotifications')}?from=${encodeURIComponent(asPath)}`}
          title={t('sidebar.settings')}
          selected={isSelected('settingsNotifications')}
          Icon={Settings}
          isSidebarExpanded={isSidebarExpanded}
        />
      }
      footer={
        canCreateRequest ? (
          isSidebarExpanded ? (
            <InternalLinkButton
              href={CREATE_REQUEST_HREF}
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={trackCreateRequest}
              sx={{ width: '100%' }}
            >
              {t('sidebar.create-request')}
            </InternalLinkButton>
          ) : (
            <InternalLinkIconButton href={CREATE_REQUEST_HREF} color="primary" onClick={trackCreateRequest}>
              <AddCircleOutline />
            </InternalLinkIconButton>
          )
        ) : null
      }
    />
  )
}
